import { BlogEditorRightToolBarActionMarkEnum } from '@doltech/utils/lib/constants';
import { pipe } from '@doltech/utils/lib/pipe';
import { noop } from 'lodash';
import * as React from 'react';
import { useDebounce } from 'react-use';
import { Editor, Range } from 'slate';
import { ReactEditor } from 'slate-react';

import { ViewVocabFlowPlugin } from '../../shared/components/IELTSample/ViewVocabFlow/plugins/ViewVocabFlowPlugin';
import { AlignPlugin } from '../../slatePlugins/elements/align/AlignPlugin';
import { HeadingPlugin } from '../../slatePlugins/elements/heading/HeadingPlugin';
import { SoftBreakPlugin } from '../../slatePlugins/handlers/soft-break/SoftBreakPlugin';
import { BoldPlugin } from '../../slatePlugins/marks/bold/BoldPlugin';
import { ItalicPlugin } from '../../slatePlugins/marks/italic/ItalicPlugin';
import { StrikethroughPlugin } from '../../slatePlugins/marks/strikethrough/StrikethroughPlugin';
import { UnderlinePlugin } from '../../slatePlugins/marks/underline/UnderlinePlugin';
import { withInlineVoid } from '../../slatePlugins/plugins/inline-void';
import { withDeserializeHTML } from '../DolEditor/deserializers/html-deserializer/withDeserializeHTML';
import { useDolEditorStore } from '../DolEditor/dol-editor.store';
import { BorderShadingElementPlugin } from '../DolEditor/elements/border-shading/plugin';
import { DEFAULT_HIGHLIGHT_COLORS_LIST } from '../DolEditor/elements/highlight/defaults';
import { HighlightElementPlugin } from '../DolEditor/elements/highlight/plugin';
import { withHighlightPlugin } from '../DolEditor/elements/highlight/withHighlightPlugin';
import { HoverCardElementPlugin } from '../DolEditor/elements/hover-card/plugin';
import { HyperLinkElementPlugin } from '../DolEditor/elements/hyper-link/plugin';
import { IconPlugin } from '../DolEditor/elements/icons/IconPlugin';
import { RoundedElementPlugin } from '../DolEditor/elements/rounded/plugin';
import { withRoundedPlugin } from '../DolEditor/elements/rounded/withRoundedPlugin';
import { BlogTablePlugin } from '../DolEditor/elements/table-blog/BlogTablePlugin';
import { withTableV2 } from '../DolEditor/elements/table-blog/components/AdminView/withTableV2';
import { withBreakLinePlugin } from '../DolEditor/handlers/break-line-no-style/withBreakLine';
import { ReadOnlyPlugin } from '../DolEditor/handlers/read-only/ReadOnlyPlugin';
import { BackgroundColorPlugin } from '../DolEditor/marks/background-color/BackgroundColorPlugin';
import { TextColorPlugin } from '../DolEditor/marks/text-color/TextColorPlugin';
import { UnderlineColorPlugin } from '../DolEditor/marks/underline-color/UnderlineColorPlugin';
import { DisableEnterPlugin } from '../DolEditor/toolbars/disable-enter/DisableEnterPlugin';
import { useBlogEditorStore } from './blog-editor.store';
import { DEFAULTS_OPTIONS } from './editorDefaultOptions';
import {
  BulletPointPlugin,
  withBulletPointPluginEditorChain,
} from './plugins/BulletPoint/BulletPointPlugin';
import { DivPlugin, withDivEditorChains } from './plugins/DivPlugin';
import { EnterToScrollPlugin } from './plugins/enterToScrollPlugin';
import {
  QuestionStylePlugin,
  withQuestionStyleEditorChain,
} from './plugins/QuestionStyle/QuestionStyle';
import { QuotePlugin } from './plugins/Quote/QuotePlugin';
import { TextTransformPlugin } from './plugins/TextTransform/TextTransformPlugin';
import { withDeleteAtEndNode } from './plugins/withDeleteAtEndNode';
import { withParagraphAtEditorEnd } from './plugins/withParagraphAtEditorEnd';
import { patchFixInvalidNode, trimEmptyParagraph } from './shared-components/utils';
import { UploadImagePlugin } from '../DolEditor/elements/UploadImage/plugin';

export const useBlogEditorPlugin = ({
  editor,
  isReadOnly = false,
  onBackWhenInComplete = noop,
  extraPlugins = [],
  extraEditorChains = [],
  overrideRightToolbar,
}: UseBlogEditorPluginProps) => {
  const options = DEFAULTS_OPTIONS;
  const dolEditorStore = useDolEditorStore();
  const blogEditorStore = useBlogEditorStore();

  const headingEnabled = React.useMemo(() => {
    let isSupportHeading = true;
    if (overrideRightToolbar && overrideRightToolbar?.length > 0) {
      const allActions = overrideRightToolbar.reduce((p, n) => p.concat(n.actions), []);
      isSupportHeading = allActions.some((action) =>
        [BlogEditorRightToolBarActionMarkEnum.H2, BlogEditorRightToolBarActionMarkEnum.H3, BlogEditorRightToolBarActionMarkEnum.H4].includes(
          action
        )
      );
    }
    return isSupportHeading;
  }, [overrideRightToolbar]);

  const plugins = React.useMemo(() => {
    const corePlugins = [
      // core
      DivPlugin({ headingEnabled }),
      DisableEnterPlugin(),
      EnterToScrollPlugin(),
      SoftBreakPlugin({
        rules: [
          {
            hotkey: 'shift+enter',
          },
        ],
      }),
    ];

    const textFormatPlugins = [
      // text format (leaf)
      AlignPlugin(),
      BoldPlugin(options),
      ItalicPlugin(options),
      headingEnabled && HeadingPlugin(options), // h1, h2, h3, h4, h5

      HighlightElementPlugin({
        colors: DEFAULT_HIGHLIGHT_COLORS_LIST,
      }),
      StrikethroughPlugin(options),
      TextTransformPlugin(), // uppercase, titlecase, lowercase
      QuestionStylePlugin(), // a rounded circle text to indicate question number
      RoundedElementPlugin(), // rounded text with annotation
      TextColorPlugin(),
      UnderlinePlugin(options),
      UnderlineColorPlugin(),
      HyperLinkElementPlugin({ isReadOnly }),
      ViewVocabFlowPlugin({ isReadOnly }),

      IconPlugin(), // emoji - old, least used
      UploadImagePlugin({ isPreview: isReadOnly }),
    ];

    const usedPlugins = [
      isReadOnly ? ReadOnlyPlugin() : undefined,

      ...textFormatPlugins,

      // block format (node)
      BorderShadingElementPlugin({ isReadOnly }),
      BackgroundColorPlugin(), // node level - mostly use for border and shading
      HoverCardElementPlugin({ isReadOnly }), // anchor link which have popup info

      // html object
      BulletPointPlugin({ isReadOnly }),

      BlogTablePlugin({ isReadOnly, stickyHeaderOffset: 64 }),
      QuotePlugin({ isReadOnly }),

      ...extraPlugins,
      ...corePlugins,
    ];

    return usedPlugins.filter(Boolean);
  }, [extraPlugins, headingEnabled, isReadOnly, options]);

  const editorChains = React.useMemo(() => {
    return [
      // core
      withInlineVoid({ plugins }),
      withDeserializeHTML({ plugins }), // This must be above any plugins handle copy/paste ( so it won't add default html handling )
      ...extraEditorChains,
      withBreakLinePlugin(), // this must ahead of list plugin ( so it won't override insert break behavior of list )
      withDivEditorChains(),
      withDeleteAtEndNode(),
      withParagraphAtEditorEnd(),

      // text format (leaf)
      withHighlightPlugin(),
      withRoundedPlugin(),
      withQuestionStyleEditorChain(),

      // html object
      withTableV2(),
      withBulletPointPluginEditorChain(),
    ].filter(Boolean);
  }, [extraEditorChains, plugins]);

  useDebounce(
    () => {
      if (isReadOnly) return;
      if (editor) {
        const { selection } = editor;
        const isFocused = ReactEditor.isFocused(editor);
        if (selection && isFocused) {
          blogEditorStore.selectState.disableAll(false);
          blogEditorStore.selectState.reset();
          const nodeEntries = Editor.nodes(editor, {
            at: editor?.selection,
          });

          const isTextSelection = Range.isExpanded(selection);

          blogEditorStore.selectState.updateIsTextSelection(isTextSelection);
          for (const [node, path] of nodeEntries) {
            blogEditorStore.selectState.markSelectionCheck({ node, path });
            blogEditorStore.selectState.cardLogicSelectionCheck({ node, path });
            blogEditorStore.selectState.tableSelectionCheck({ node, path });
            blogEditorStore.selectState.borderShadingSelectionCheck({ node });
            blogEditorStore.selectState.vocabCardCheck({ node });
          }
          blogEditorStore.selectState.doneCheckSelectState();
        } else {
          blogEditorStore.selectState.disableAll();
        }
      }
    },
    200,
    [dolEditorStore?.afterSelectionId]
  );

  const preprocessReadOnlyInitialValues = ({ initialValue }) => {
    return pipe(patchFixInvalidNode, trimEmptyParagraph)(initialValue);
  };

  const preprocessEditorInitialValues = ({ initialValue }) => {
    return patchFixInvalidNode(initialValue);
  };

  return {
    plugins,
    editorChains,
    preprocessReadOnlyInitialValues,
    preprocessEditorInitialValues,
  };
};

interface UseBlogEditorPluginProps {
  editor: any;
  isReadOnly?: boolean;
  onBackWhenInComplete?: () => void;
  extraPlugins?: any;
  extraEditorChains?: any;
  overrideRightToolbar?: any;
}

export const useInputCaptionPlugin = ({ isReadOnly = false, overrideRightToolbar = null }) => {
  return useBlogEditorPlugin({
    editor: null,
    isReadOnly,
    overrideRightToolbar,
  });
};

export const useTextOnlyEditorPlugin = useInputCaptionPlugin;
